import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { useNodeSelection } from "@/contexts/node-selection";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function NodeFinder() {
  const [open, setOpen] = useState(false);
  const [stakeAmount, setStakeAmount] = useState("");
  const { setSelectedNode } = useNodeSelection();

  const { data: matchingNode } = useQuery({
    queryKey: ["findNode", stakeAmount],
    queryFn: async () => {
      if (!stakeAmount || isNaN(Number(stakeAmount))) return null;
      
      // First, get all nodes
      const { data: nodes } = await supabase
        .from("provisioners")
        .select("id, address, current_stake")
        .gt('current_stake', 0) // Only get nodes with positive stake
        .order('current_stake', { ascending: true });

      if (!nodes || nodes.length === 0) return null;

      // Find the node with the closest stake amount
      const targetStake = Number(stakeAmount);
      const closestNode = nodes.reduce((prev, curr) => {
        const prevDiff = Math.abs(prev.current_stake - targetStake);
        const currDiff = Math.abs(curr.current_stake - targetStake);
        return currDiff < prevDiff ? curr : prev;
      });

      return closestNode;
    },
    enabled: open && stakeAmount !== "",
  });

  const handleSelectNode = async () => {
    if (!matchingNode) return;

    const node = {
      id: matchingNode.id,
      address: matchingNode.address,
    };

    setSelectedNode(node);
    setOpen(false);
    setStakeAmount("");
    toast.success("Node selected based on stake amount");
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-[200px] justify-between"
            >
              <span>Find by stake</span>
              <Search className="h-4 w-4 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="number"
                  placeholder="Enter stake amount"
                  value={stakeAmount}
                  onChange={(e) => setStakeAmount(e.target.value)}
                />
              </div>
              {matchingNode && (
                <div className="space-y-2">
                  <p className="text-sm">Closest match:</p>
                  <p className="text-xs text-muted-foreground">
                    Stake: {matchingNode.current_stake.toLocaleString()} DUSK
                  </p>
                  <Button
                    className="w-full"
                    size="sm"
                    onClick={handleSelectNode}
                  >
                    Select Node
                  </Button>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-sm">Find a node based on how much DUSK you want to stake. Enter your desired stake amount and we'll find the closest match!</p>
      </TooltipContent>
    </Tooltip>
  );
}